<script>
import AiSideBar from "./components/AiSideBar.vue";
import SpeechToText from "./components/SpeechToText.vue";
import SvgIcon from "@/shared/ui/svg-icon/SvgIcon.vue";
import { sendChatMessage } from "@/shared/api/apiClientAi";
import { getTemplateItem } from "@/entities/template";
import { CATEGORY_CODES } from "@/shared/lib/constants";
import { Loading } from "@element-plus/icons-vue";
import { safeMarkdown } from "@/shared/lib/markdown/safeMarkdown";
import moment from "moment";

export default {
  name: "AiAssistantPage",
  components: {
    AiSideBar,
    SpeechToText,
    SvgIcon,
    Loading,
  },
  data() {
    return {
      loading: false,
      sessions: [],
      currentSession: null,
      abortController: null,
      message: "",
      inputMode: "text",
      isAtBottom: true,
    };
  },
  created() {
    const storedSessions = localStorage.getItem("ai_assistant_sessions");
    let parsedSessions = [];
    if (storedSessions) {
      try {
        parsedSessions = JSON.parse(storedSessions);
      } catch (e) {
        console.error("Ошибка парсинга ai_assistant_sessions:", e);
      }
    }

    const oneWeekAgo = moment().subtract(7, "days").valueOf();
    parsedSessions = parsedSessions.filter(
      (session) => session.createdAt >= oneWeekAgo
    );
    this.sessions = parsedSessions;
    this.createNewSession();
  },
  mounted() {
    this.$refs.textarea?.focus();
    this.attachScrollListener();
  },
  beforeUnmount() {
    this.detachScrollListener();
    if (this._scrollRAF) cancelAnimationFrame(this._scrollRAF);
    this._scrollCancel?.();
  },
  watch: {
    sessions: {
      deep: true,
      handler(newVal) {
        const sessionsWithMessages = newVal.filter(
          (session) => session.messages && session.messages.length > 0
        );
        localStorage.setItem(
          "ai_assistant_sessions",
          JSON.stringify(sessionsWithMessages)
        );
      },
    },
    message() {
      this.$nextTick(() => this.adjustTextareaHeight());
    },
    history: {
      deep: true,
      handler() {
        this.$nextTick(() => {
          this._onScroll?.();
        });
      },
    },
  },
  computed: {
    history() {
      if (!this.currentSession) return [];
      return this.currentSession.messages;
    },
  },
  methods: {
    renderMessage(msg) {
      if (!msg) return "";
      return safeMarkdown(msg);
    },
    formatTimestamp(ts) {
      return moment(ts).format("HH:mm");
    },

    // --- Скролл ---
    attachScrollListener() {
      const el = this.$refs.chatBody;
      if (!el) return;
      this._onScroll = () => {
        const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
        this.isAtBottom = remaining < el.clientHeight * 0.3;
      };
      el.addEventListener("scroll", this._onScroll, { passive: true });
      this._onScroll();
    },
    detachScrollListener() {
      const el = this.$refs.chatBody;
      if (el && this._onScroll) {
        el.removeEventListener("scroll", this._onScroll);
      }
    },
    _animateScroll(target, duration) {
      const el = this.$refs.chatBody;
      if (!el) return;
      if (this._scrollRAF) cancelAnimationFrame(this._scrollRAF);
      if (this._scrollCancel) this._scrollCancel();
      const start = el.scrollTop;
      if (target - start < 2) return;
      const startTime = performance.now();
      let cancelled = false;
      const onUserScroll = () => {
        cancelled = true;
      };
      el.addEventListener("wheel", onUserScroll, { passive: true });
      el.addEventListener("touchstart", onUserScroll, { passive: true });
      el.addEventListener("keydown", onUserScroll);
      this._scrollCancel = () => {
        cancelled = true;
        el.removeEventListener("wheel", onUserScroll);
        el.removeEventListener("touchstart", onUserScroll);
        el.removeEventListener("keydown", onUserScroll);
      };
      const step = (now) => {
        if (cancelled) {
          this._scrollCancel?.();
          this._scrollCancel = null;
          return;
        }
        const t = Math.min((now - startTime) / duration, 1);
        const eased = 1 - Math.pow(1 - t, 3);
        el.scrollTop = start + (target - start) * eased;
        if (t < 1) {
          this._scrollRAF = requestAnimationFrame(step);
        } else {
          this._scrollCancel?.();
          this._scrollCancel = null;
        }
      };
      this._scrollRAF = requestAnimationFrame(step);
    },
    slowScrollToBottom(duration = 700) {
      const el = this.$refs.chatBody;
      if (!el) return;
      const target = el.scrollHeight - el.clientHeight;
      this._animateScroll(target, duration);
    },
    slowScrollPartial(fraction = 0.5, duration = 600) {
      const el = this.$refs.chatBody;
      if (!el) return;
      const maxScroll = el.scrollHeight - el.clientHeight;
      const target = Math.min(
        el.scrollTop + el.clientHeight * fraction,
        maxScroll
      );
      this._animateScroll(target, duration);
    },
    scrollLastMessageToTop() {
      const el = this.$refs.chatBody;
      if (!el) return;
      const groups = el.querySelectorAll(".chat-message-group");
      const last = groups[groups.length - 1];
      if (!last) return;
      const containerRect = el.getBoundingClientRect();
      const messageRect = last.getBoundingClientRect();
      const target = el.scrollTop + (messageRect.top - containerRect.top) - 16;
      el.scrollTo({ top: target, behavior: "smooth" });
    },
    ensurePlaceholderVisible() {
      const el = this.$refs.chatBody;
      if (!el) return;
      const groups = el.querySelectorAll(".chat-message-group");
      const last = groups[groups.length - 1];
      if (!last) return;
      const dock = el.querySelector(".chat-dock");
      const dockHeight = dock?.offsetHeight ?? 150;
      const safeBottom = el.clientHeight - (dockHeight + 40);
      const containerRect = el.getBoundingClientRect();
      const lastRect = last.getBoundingClientRect();
      const lastBottomRelative = lastRect.bottom - containerRect.top;
      if (lastBottomRelative > safeBottom) {
        const delta = lastBottomRelative - safeBottom;
        el.scrollTo({
          top: el.scrollTop + delta,
          behavior: "smooth",
        });
      }
    },

    // --- Отправка сообщений ---
    async sendMessage(text) {
      const msg = text || this.message;
      if (!msg || !msg.trim()) return;
      if (this.loading) return;

      this.loading = true;
      this.abortController = new AbortController();
      this.message = "";
      this.$nextTick(() => this.adjustTextareaHeight());

      if (this.currentSession && this.currentSession.messages.length === 0) {
        this.currentSession.title = msg;
      }

      this.history.push({
        sender: "user",
        message: msg,
        timestamp: Date.now(),
      });

      await this.$nextTick();
      this.scrollLastMessageToTop();

      const assistantPlaceholder = {
        sender: "assistant",
        message: "",
        timestamp: `${Date.now()}_${Math.floor(Math.random() * 100000)}`,
        isTyping: true,
      };

      this.history.push(assistantPlaceholder);
      await this.$nextTick();
      this.ensurePlaceholderVisible();

      try {
        const response = await sendChatMessage(msg, this.currentSession?.id, {
          signal: this.abortController.signal,
        });
        const data = response.data;

        const idx = this.history.indexOf(assistantPlaceholder);
        if (idx !== -1) this.history.splice(idx, 1);

        this.history.push({
          sender: "assistant",
          timestamp: Date.now(),
          message:
            data.question ||
            "Извините, не удалось обработать ваш запрос. Попробуйте переформулировать.",
          categories: data.categories || null,
        });
        await this.$nextTick();
        this.slowScrollPartial(0.5);
      } catch (error) {
        if (error.name !== "AbortError" && error.code !== "ERR_CANCELED") {
          console.error(error);
          const idx = this.history.indexOf(assistantPlaceholder);
          if (idx !== -1) this.history.splice(idx, 1);
          this.history.push({
            sender: "assistant",
            timestamp: Date.now(),
            message:
              "Мне не удалось обработать ваш запрос. Возможно, дело в данных или во временной ошибке сервиса. Пожалуйста, попробуйте ещё раз немного позже",
          });
        }
      } finally {
        this.loading = false;
        this.abortController = null;
        if (this.currentSession) {
          this.currentSession.messages = this.history.filter(
            (item) => !item.isTyping
          );
        }
      }
    },

    // --- Форма ввода ---
    handleEnter(e) {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        this.sendMessage();
      }
    },
    adjustTextareaHeight() {
      const textarea = this.$refs.textarea;
      if (!textarea) return;
      textarea.style.height = "auto";
      textarea.style.height = Math.min(textarea.scrollHeight, 200) + "px";
    },
    toggleVoice() {
      if (this.inputMode === "text") {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          console.warn(
            "getUserMedia не поддерживается в этом браузере или контексте"
          );

          return;
        }
        navigator.mediaDevices
          .getUserMedia({ audio: true })
          .then(() => {
            this.inputMode = "voice";
          })
          .catch(() => {
            this.inputMode = "text";
          });
      } else {
        this.inputMode = "text";
      }
    },
    handleSttResult(text) {
      this.inputMode = "text";
      if (text) {
        this.message = text;
        this.sendMessage();
      }
    },

    // --- Hover-действия ---
    copyMessage(msg) {
      if (msg) navigator.clipboard.writeText(msg);
    },

    // --- Категории ---
    handleSelectCategory(category) {
      this.navigateToAction(category.code);
    },
    async navigateToAction(code) {
      const data = await getTemplateItem(code);
      if (data?.payload?.category_code === CATEGORY_CODES.SVID_PRAVO_NASLED) {
        this.$router.push({ name: "hereditary_case_new", query: { code } });
      } else {
        this.$router.push({ name: "notarial_action_new", query: { code } });
      }
    },

    // --- Сессии ---
    cancelProcessing(silent = false) {
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
        this.loading = false;
        if (this.currentSession) {
          this.currentSession.messages = this.currentSession.messages.filter(
            (m) => !m.isTyping
          );
        }
        if (!silent) {
          this.history.push({
            sender: "assistant",
            message: this.$t(
              "Выполнение вашего запроса было остановлено. Чтобы продолжить, отправьте новое сообщение."
            ),
            timestamp: Date.now(),
          });
        }
      }
    },
    newChat() {
      if (this.history.length > 0) {
        this.createNewSession();
      }
      this.message = "";
      this.inputMode = "text";
    },
    createNewSession() {
      this.cancelProcessing(true);
      const newSession = {
        id: Date.now(),
        createdAt: Date.now(),
        title: this.$t("Новый чат"),
        messages: [],
      };
      this.sessions.unshift(newSession);
      this.currentSession = newSession;
    },
    setActiveSession(session) {
      if (this.currentSession && !this.currentSession.messages.length) {
        this.sessions = this.sessions.filter(
          (s) => s.id !== this.currentSession.id
        );
      }
      this.currentSession = session;
    },
    deleteSession(session) {
      this.sessions = this.sessions.filter((s) => s !== session);
      if (this.currentSession === session) {
        this.currentSession = null;
        this.createNewSession();
      }
    },
    clearChat() {
      this.sessions = [];
      this.currentSession = null;
      localStorage.removeItem("ai_assistant_sessions");
      this.createNewSession();
    },
  },
};
</script>

<template>
  <div class="ai-assistant-layout">
    <ai-side-bar
      :sessions="sessions"
      :current-session="currentSession"
      @newChat="newChat"
      @update:currentSession="setActiveSession"
      @delete-session="deleteSession"
      @clearHistory="clearChat"
    />

    <div class="ai-assistant-main">
      <div class="ai-assistant-chat" ref="chatBody">
        <div
          class="ai-assistant-inner"
          :class="{ 'is-empty': !history.length }"
        >
          <div
            v-if="!history.length"
            class="text-2xl font-bold text-center mb-6 text-foreground"
          >
            {{ $t("Чем я могу помочь?") }}
          </div>

          <div v-if="history.length" class="chat-messages">
            <div
              v-for="item in history"
              :key="item.timestamp"
              class="chat-message-group"
            >
              <div
                v-if="item.sender === 'user'"
                class="chat-bubble chat-bubble--user"
              >
                <div
                  v-html="renderMessage(item.message)"
                  v-if="item.message"
                ></div>
              </div>

              <div
                v-if="item.sender === 'assistant'"
                class="chat-bubble chat-bubble--assistant"
              >
                <div v-if="item.isTyping" class="chat-loading">
                  <el-icon class="is-loading"><Loading /></el-icon>
                  <span>{{ $t("Подбираю подходящие услуги") }}...</span>
                </div>

                <div
                  v-if="
                    item.message &&
                    !item.isTyping &&
                    !(item.categories && item.categories.length)
                  "
                  class="chat-assistant-text"
                  v-html="renderMessage(item.message)"
                ></div>

                <template v-if="item.categories && item.categories.length">
                  <div class="chat-forms-header">
                    {{ $t("Найдено услуг") }}: {{ item.categories.length }}.
                    {{ $t("Выберите подходящую") }}:
                  </div>
                  <div class="chat-forms-list">
                    <button
                      v-for="cat in item.categories"
                      :key="cat.code"
                      class="chat-form-card"
                      @click="handleSelectCategory(cat)"
                    >
                      <div class="chat-form-name">{{ cat.name }}</div>
                      <div class="chat-form-code">{{ cat.code }}</div>
                    </button>
                  </div>
                </template>
              </div>

              <div
                v-if="!item.isTyping"
                class="chat-timestamp"
                :class="item.sender"
              >
                {{ formatTimestamp(item.timestamp) }}
              </div>
            </div>
          </div>

          <div class="chat-dock">
            <button
              v-if="!isAtBottom && history.length"
              class="chat-scroll-bottom"
              @click="slowScrollToBottom(700)"
              :title="$t('Прокрутить вниз')"
              aria-label="Scroll to bottom"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M12 5v14M19 12l-7 7-7-7" />
              </svg>
            </button>
            <div class="chat-dock-container">
              <div v-if="inputMode === 'text'" class="chat-form">
                <textarea
                  v-model="message"
                  ref="textarea"
                  rows="1"
                  @keydown="handleEnter"
                  :placeholder="$t('Введите Ваш вопрос ...')"
                ></textarea>
                <div class="chat-form-actions">
                  <div></div>
                  <div class="flex items-center gap-2">
                    <button
                      v-if="loading"
                      @click="cancelProcessing()"
                      class="cancel-btn"
                    >
                      <SvgIcon name="huge-close" class="w-4 h-4" />
                    </button>
                    <template v-else>
                      <button
                        class="voice-btn"
                        @click="toggleVoice"
                        :title="$t('Записать голос')"
                      >
                        <SvgIcon name="sli-microphone" class="w-5 h-5" />
                      </button>
                      <button
                        class="send-btn"
                        :class="{ active: message.length > 0 }"
                        @click="sendMessage()"
                        :title="$t('Отправить')"
                      >
                        <SvgIcon name="sli-send" class="w-5 h-5" />
                      </button>
                    </template>
                  </div>
                </div>
              </div>
              <SpeechToText v-else @finish="handleSttResult" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.ai-assistant-layout {
  @apply flex h-full w-full border border-border rounded-lg overflow-hidden;
}

.ai-assistant-main {
  @apply flex-1 flex flex-col;
  position: relative;
  min-width: 0;
  min-height: 0;
}

.ai-assistant-chat {
  @apply w-full relative flex justify-center overflow-y-auto;
  flex: 1 1 0;
  min-height: 0;
  padding: 0 24px;
}

.ai-assistant-inner {
  @apply w-full flex flex-col;
  max-width: 760px;
  min-height: 100%;

  &.is-empty {
    justify-content: center;

    .chat-dock {
      position: static;
      margin-top: 0;
    }
  }
}

.chat-dock {
  position: sticky;
  bottom: 0;
  z-index: 10;
  margin-top: auto;
}
.chat-dock-container {
  position: relative;
  width: 100%;
  padding-bottom: max(16px, env(safe-area-inset-bottom));
  z-index: 1;
  background: hsl(var(--background));
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
}
.chat-scroll-bottom {
  position: absolute;
  left: 50%;
  bottom: 100%;
  transform: translate(-50%, -8px);
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 1px solid hsl(var(--border));
  background: hsl(var(--background));
  color: hsl(var(--foreground));
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 2;
  box-shadow: 0 4px 12px rgb(0 0 0 / 0.08), 0 1px 3px rgb(0 0 0 / 0.06);
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.15s ease;

  &:hover {
    transform: translate(-50%, -10px);
    background: hsl(var(--muted));
    box-shadow: 0 6px 16px rgb(0 0 0 / 0.12), 0 2px 4px rgb(0 0 0 / 0.08);
  }

  svg {
    width: 16px;
    height: 16px;
  }
}

/* Сообщения */
.chat-messages {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 16px 0;
}

.chat-message-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.chat-bubble {
  @apply bg-muted;
  padding: 14px 14px;
  border-radius: 12px;
  font-size: 13px;
  line-height: 1.5;
  max-width: 85%;
  min-width: 0;
  position: relative;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.chat-bubble--user {
  padding: 6px 14px;
  align-self: flex-end;
  background: hsl(var(--primary));
  color: hsl(var(--primary-foreground));
  border-bottom-right-radius: 4px;
}

.chat-bubble--assistant {
  align-self: flex-start;
  @apply bg-muted/40 border border-border;
  color: hsl(var(--foreground));
  border-bottom-left-radius: 4px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 85%;
  max-width: 85%;
}

.chat-assistant-text {
  :deep(p) {
    margin-bottom: 8px;
    &:last-child {
      margin-bottom: 0;
    }
  }
  :deep(ul),
  :deep(ol) {
    padding-left: 20px;
    margin-bottom: 8px;
  }
  :deep(h3) {
    font-size: 15px;
    font-weight: 600;
    margin: 12px 0;
  }
  :deep(strong) {
    font-weight: 600;
  }
  :deep(hr) {
    margin: 12px 0;
    border-color: hsl(var(--border));
  }
  :deep(a) {
    overflow-wrap: anywhere;
    word-break: break-word;
  }
  :deep(pre) {
    max-width: 100%;
    overflow-x: auto;
    white-space: pre-wrap;
    word-break: break-word;
  }
  :deep(code) {
    overflow-wrap: anywhere;
    word-break: break-word;
  }
}

.chat-timestamp {
  font-size: 11px;
  color: hsl(var(--muted-foreground));

  &.user {
    align-self: flex-end;
  }

  &.assistant {
    align-self: flex-start;
  }
}

/* Hover-действия */
.chat-hover-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 2px;
  opacity: 0;
  transition: opacity 0.2s;

  button {
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    border: none;
    background: transparent;
    color: hsl(var(--muted-foreground));
    cursor: pointer;
    transition: background 0.15s, color 0.15s;

    &:hover {
      background: hsl(var(--muted));
      color: hsl(var(--foreground));
    }

    svg {
      fill: none;
    }
  }
}

.chat-message-group:hover .chat-hover-actions {
  opacity: 1;
}

/* Категории */
.chat-forms-header {
  font-size: 13px;
  color: hsl(var(--muted-foreground));
}

.chat-forms-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.chat-form-card {
  text-align: left;
  padding: 10px 12px;
  border: 1px solid hsl(var(--border));
  border-radius: 8px;
  background: hsl(var(--background));
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s, transform 0.15s;

  &:hover {
    border-color: hsl(var(--primary));
    box-shadow: 0 2px 8px hsl(var(--primary) / 0.08);
    transform: translateY(-1px);
  }
}

.chat-form-name {
  font-size: 13px;
  font-weight: 600;
  color: hsl(var(--foreground));
  line-height: 1.3;
}

.chat-form-code {
  font-size: 11px;
  color: hsl(var(--muted-foreground));
  margin-top: 2px;
  line-height: 1.35;
}

/* Loading */
.chat-loading {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: hsl(var(--muted-foreground));

  .is-loading {
    display: inline-flex;
  }
}

.chat-form {
  display: flex;
  flex-direction: column;
  border: 1px solid hsl(var(--border));
  border-radius: 16px;
  background: hsl(var(--background));

  textarea {
    outline: none;
    width: 100%;
    padding: 16px 16px 8px;
    border-radius: 16px;
    font-size: 14px;
    resize: none;
    background: transparent;
    color: hsl(var(--foreground));
    overflow-y: auto;
    min-height: 70px;
    max-height: 200px;

    &::placeholder {
      color: hsl(var(--muted-foreground));
    }
  }

  .chat-form-actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 16px 12px;
  }

  .cancel-btn {
    width: 28px;
    height: 28px;
    border: none;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    background: hsl(var(--muted-foreground));
    color: hsl(var(--background));

    &:hover {
      opacity: 0.8;
    }

    svg {
      fill: none;
    }
  }

  .voice-btn {
    width: 28px;
    height: 28px;
    border: none;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    background: transparent;
    color: hsl(var(--muted-foreground));

    &:hover {
      background: hsl(var(--muted));
    }

    svg {
      fill: none;
    }
  }

  .send-btn {
    width: 28px;
    height: 28px;
    border: none;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    background: hsl(var(--muted));
    color: hsl(var(--background));

    svg {
      fill: none;
    }

    &.active {
      background: hsl(var(--foreground));
    }

    &:hover {
      background: hsl(var(--foreground) / 0.8);
    }
  }
}
</style>
